/**
 * Constantes del dominio de Actividad de usuarios: permiso de gating, períodos del
 * filtro (réplica del SSR) y etiquetas legibles de módulos/roles.
 *
 * El gating de la UI usa el MISMO permiso que exige la API y el SSR
 * (`advanced_stats` para las tres rutas de actividad; ver
 * `application/api/domains/user_activity.py` y `progress/explore_strangle_ssr.md`).
 *
 * Las etiquetas de módulos/roles y el orden de los períodos replican EXACTAMENTE
 * los de `templates/user_activity.html` (los diccionarios `MOD`, `ROLE_L` y las
 * pestañas), para no inventar una nomenclatura distinta a la que el usuario conoce.
 */
import type { ActivityPeriod } from "./schemas";

/** Permiso que gobierna la actividad de usuarios (igual que la API y el SSR). */
export const ADVANCED_STATS_PERMISSION = "advanced_stats";

/** Una opción del filtro de período (valor de la API + etiqueta del SSR). */
export interface PeriodOption {
  value: ActivityPeriod;
  label: string;
}

/**
 * Los 5 períodos del SSR, en su orden y con sus etiquetas (las pestañas
 * "Hoy/Esta semana/Este mes/Últimos 7 días/Histórico" de `user_activity.html`).
 */
export const PERIOD_OPTIONS: PeriodOption[] = [
  { value: "day", label: "Hoy" },
  { value: "week", label: "Esta semana" },
  { value: "month", label: "Este mes" },
  { value: "7days", label: "Últimos 7 días" },
  { value: "all", label: "Histórico" },
];

/** Período por defecto al abrir la pantalla (igual que el SSR: "Hoy"). */
export const DEFAULT_PERIOD: ActivityPeriod = "day";

/**
 * Ventana en días que el SSR pasa al detalle según el período activo (réplica de la
 * función `abrirDetalle` de `user_activity.html`). El detalle de la API recibe
 * `days`; este mapeo conserva la misma semántica que el SSR.
 */
export const PERIOD_TO_DETAIL_DAYS: Record<ActivityPeriod, number> = {
  day: 1,
  week: 7,
  month: 30,
  "7days": 7,
  all: 90,
};

/**
 * Etiquetas legibles de los módulos (diccionario `MOD` del SSR). Un módulo no
 * listado se muestra con su id crudo (`moduleLabel` en `format.ts`).
 */
export const MODULE_LABELS: Record<string, string> = {
  ordenes: "Órdenes",
  horario: "Horario",
  horario_laser: "Láser",
  pedidos_laminas: "Ped. Láminas",
  inventario: "Inventario",
  estadisticas: "Estadísticas",
  talonarios: "Talonarios",
  cotizador: "Cotizador",
  ver_lista: "Lista órdenes",
  nueva_orden: "Nueva orden",
};

/**
 * Etiquetas legibles de los roles (diccionario `ROLE_L` del SSR). Un rol no listado
 * se muestra con su id crudo (`roleLabel` en `format.ts`).
 */
export const ROLE_LABELS: Record<string, string> = {
  admin: "Admin",
  supervisor: "Supervisor",
  caja: "Caja",
  logistica: "Logística",
  operario: "Operario",
  entregas: "Entregas",
  mostrador: "Mostrador",
};

/** Mensaje genérico ante un error inesperado (no-API). */
export const ACTIVITY_GENERIC_ERROR = "Ocurrió un error inesperado.";
